import { useState } from 'react'
import { Spinner } from 'react-bootstrap'
import {
  useChangeMiscSettings,
  useGetPreferences,
  useUpdateReportingUnit,
} from 'services/preferences'
import { Toggle } from 'components/FbUI/Toggle'
import { Select } from 'components/UI/Select/Select'
import { MISC_SETTINGS } from 'models/misc_settings'
import { queryClient } from 'services/queryClient'
import { notifySuccess, notifyError } from 'services/toast'
import { REPORTING_UNITS } from 'constants/reportingUnits'

const PAGE_DESCRIPTION =
  'Customize your First Bite experience with the following preferences.'

export function MiscSettingsPage() {
  const mutation = useChangeMiscSettings()
  const updateReportingUnitMutation = useUpdateReportingUnit()
  const { data, isLoading } = useGetPreferences()

  const [isApplyingTempClosed, setIsApplyingTempClosed] = useState(false)

  if (isLoading || !data) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Spinner animation="border" />
      </div>
    )
  }

  const showTempClosed = data.misc_settings?.find(
    (s) => s.key === 'show_temp_closed'
  )
  const reportingUnit = data.reporting_unit ?? ''
  const tempClosedEnabled = !!parseInt(showTempClosed?.value ?? '0')

  async function handleChangeTempClosed(val: boolean) {
    setIsApplyingTempClosed(true)
    try {
      await mutation.mutateAsync({
        key: 'show_temp_closed',
        data: { value: val ? '1' : '0' },
      })
      notifySuccess('Setting Changed Successfully')
      queryClient.clear()
    } catch {
      notifyError('Failed to update setting')
    } finally {
      setIsApplyingTempClosed(false)
    }
  }

  async function handleUpdateReportingUnit(newValue: string) {
    if (newValue === reportingUnit) return
    if (!newValue) {
      notifyError('Unit cannot be blank')
      return
    }
    try {
      await updateReportingUnitMutation.mutateAsync({
        data: { reporting_unit: newValue },
      })
      notifySuccess('Unit Updated Successfully')
      queryClient.invalidateQueries({ queryKey: ['preferences'] })
    } catch {
      notifyError('Failed to update unit')
    }
  }

  return (
    <div className="w-full h-full overflow-y-auto">
      <div className="max-w-3xl">
        <p className="text-sm text-gray-500 mb-8">{PAGE_DESCRIPTION}</p>

        <div className="space-y-8">
          <SettingSection
            title={MISC_SETTINGS.show_temp_closed.title}
            description={MISC_SETTINGS.show_temp_closed.description}
            control={
              <Toggle
                checked={tempClosedEnabled}
                onChange={handleChangeTempClosed}
                disabled={isApplyingTempClosed}
                aria-label={MISC_SETTINGS.show_temp_closed.title}
              />
            }
          />

          <SettingSection
            title={MISC_SETTINGS.reporting_unit.title}
            description={MISC_SETTINGS.reporting_unit.description}
            control={
              updateReportingUnitMutation.isPending ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <Select
                  items={REPORTING_UNITS.map((unit) => ({
                    label: `${unit.singular} / ${unit.plural}`,
                    value: unit.value,
                  }))}
                  selected={reportingUnit}
                  onSelect={(newValue) =>
                    handleUpdateReportingUnit(newValue as string)
                  }
                  buttonSize="sm"
                  buttonVariant="outline"
                  buttonClassName="w-[180px]"
                  matchTriggerWidth
                  searchPlaceholder="Search units..."
                />
              )
            }
          />
        </div>
      </div>
    </div>
  )
}

function SettingSection({
  title,
  description,
  control,
}: {
  title: string
  description: string
  control: React.ReactNode
}) {
  return (
    <section className="pb-8 border-b border-gray-200 last:border-b-0 last:pb-0">
      <h3 className="text-base font-semibold text-gray-900 mb-1">{title}</h3>
      <p className="text-sm text-gray-500 leading-relaxed mb-4">
        {description}
      </p>
      <div>{control}</div>
    </section>
  )
}
